import {Router} from "@angular/router";
import {ComponentRef, ViewContainerRef} from "@angular/core";
import {Observable, timer} from "rxjs";

import {MixinConstructor} from "./mixin.define";
import {LINK_POPUP_DELAY, LinkInfo, LIST_TYPE, RESULT_LINKPOPUP, SearchParam} from "../defines/client.const";
import {DialectHistoryService} from "../services/dialect/dialect-history.service";
import {DynamicComponentService} from "../services/dynamic-component/dynamic-component.service";
import {LinkPopupComponent} from "../components/link-popup/link-popup.component";
import {LinkPopupPlacement} from "../components/link-popup/link-popup-placement";
import scrollIntoView from "scroll-into-view-if-needed";

export interface DialectLinkPopupOptions {
  placement?: LinkPopupPlacement;
  scrollIntoView?: boolean;
}

type DialectLinkMixinType = {
  router: Router,
  dHistorySvc: DialectHistoryService,
  dynamicSvc: DynamicComponentService,
};

/**
 * 단일 mixin을 사용하는 경우의 정의.
 */
export const dialectLinkMixin = DialectLinkMixinFunction(
  class Parent {
    constructor(
      public router: Router,
      public dHistorySvc: DialectHistoryService,
      public dynamicSvc: DynamicComponentService,
    ) {}
  }
);

export function DialectLinkMixinFunction<BaseType extends MixinConstructor<DialectLinkMixinType>>(BaseClass: BaseType) {
  return class extends BaseClass {
    constructor(...args: any[]) {
      super(...args);
    }

    private elRefDialectLinkPopup: ComponentRef<LinkPopupComponent> | null = null;


    /**
     *
     * @param ids - 사투리 ID 들 (콤마로 구분)
     * @param container - 링크 팝업창을 열 기준이 되는 요소
     * @param options - 링크 팝업 배치와 스크롤 보정 옵션
     */
    public OpenDialectLink(ids: string, container: ViewContainerRef, options: DialectLinkPopupOptions = {}): Observable<RESULT_LINKPOPUP> {
      console.log('DialectLinkMixin::OpenDialectLink() : dialect=', ids);

      return new Observable<RESULT_LINKPOPUP>(observer => {
        try {
          this.elRefDialectLinkPopup = this.dynamicSvc.createComponent(LinkPopupComponent, container);
          if (options.placement !== undefined) {
            // 명시적으로 지정된 경우에만 덮어쓰고, 그 외에는 컴포넌트 @Input 기본값을 그대로 사용한다.
            this.elRefDialectLinkPopup.instance.placement = options.placement;
          }

          /******************************************************************************************
           * 팝업창이 닫힐 때 발생하는 이벤트 처리.
           */
          this.elRefDialectLinkPopup.instance.close.subscribe((linkInfo: LinkInfo | null) => {
            console.log('DialectLinkMixin::OpenDialectLink() : linkInfo=', linkInfo);

            observer.next(RESULT_LINKPOPUP.POPUP_CLOSE);

            if (linkInfo && linkInfo?.dialectIds) {
              const id = linkInfo.dialectIds[0];
              if (this.dHistorySvc.getById(id) === null) {
                // 없으면 추가.
                this.dHistorySvc.addHistory(id);
              }
              const queryParams: SearchParam = {
                id,
              };
              this.router.navigate(['/dialect-list', LIST_TYPE.SERVER], {queryParams}).then();

              observer.next(RESULT_LINKPOPUP.GOTO_LINK);
            }

            observer.complete();
            this.closeDialectLinkPopup();
          });

          /******************************************************************************************
           * 화면에 초기화 된 후 (ngAfterViewInit()) 발생하는 이벤트 처리.
           */
          this.elRefDialectLinkPopup.instance.viewInit.subscribe((nativeEl: HTMLElement) => {
            if (options.scrollIntoView === false) {
              return;
            }

            // 팝업창이 열리는 애니메이션이 끝난 뒤에 스크롤 해야 정확한 위치로 스크롤이 된다.
            timer(LINK_POPUP_DELAY).subscribe(() => {
              console.log('DialectLinkMixin::OpenDialectLink() : viewInit=', nativeEl);

              scrollIntoView(nativeEl, {
                behavior: 'smooth',
                block: 'end',
                scrollMode: 'if-needed',
              });
            });
          });

          /******************************************************************************************
           * 팝업창에 표시할 정보 설정.
           */
          this.elRefDialectLinkPopup.instance.linkInfo = {
            dialectIds: ids.split(','),
          };
          this.elRefDialectLinkPopup.changeDetectorRef.detectChanges();
        } catch (err) {
          console.error('DialectLinkMixin::OpenDialectLink() : Failed to create popup', err);
          observer.error(err);
        }
      });
    }

    private closeDialectLinkPopup(): void {
      if (this.elRefDialectLinkPopup) {
        this.elRefDialectLinkPopup.destroy();
        this.elRefDialectLinkPopup = null;
      }
    }
  };
}
